import type { ResponsivePhoto } from "./media";
import { servicePhotos, serviceOgPhotos } from "./service-photos";
import { stockPhotos, stockOgPhotos } from "./stock-photos";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** Open Graph image. Always a JPEG — social scrapers do not read WebP. */
  image: string;
  /** Same photo at hero sizes, for the full-bleed detail-page header. */
  imageSet: ResponsivePhoto;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "interior-painting",
    title: "Interior Painting",
    subtitle: "Walls, ceilings, trim & cabinets",
    price: "Free Estimate",
    // Client photo: freshly painted interior walls with floor masking still down.
    image: serviceOgPhotos["interior-painting"],
    imageSet: servicePhotos["interior-painting"],
    description:
      "Clean lines, even coverage, and zero mess left behind. 4 Seasons Painting repaints living rooms, bedrooms, kitchens, ceilings, baseboards, doors, and cabinetry across the Phoenix Valley. We mask and protect every surface before a brush touches a wall, use low-VOC paints that are safe around kids and pets, and put your rooms back together before we leave.",
    features: [
      "Walls, ceilings, trim, doors & baseboards",
      "Cabinet and built-in refinishing",
      "Drywall patching & texture matching before paint",
      "Low-VOC, low-odor paints safe for occupied homes",
      "Full furniture masking and floor protection",
      "Color consultation and sample boards on request",
    ],
    process: [
      { step: "Walkthrough", desc: "We measure every room, note repairs, and confirm your color choices." },
      { step: "Prep & Protect", desc: "Furniture is covered, floors masked, and holes patched and sanded smooth." },
      { step: "Prime & Paint", desc: "Two full coats applied with cut-in edges sharp enough to skip the tape lines." },
      { step: "Final Walk", desc: "We tour every room with you and touch up anything you point at." },
    ],
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    subtitle: "Stucco, block, siding & trim",
    price: "Free Estimate",
    // Client photo: stucco exterior masked and mid-repaint.
    image: serviceOgPhotos["exterior-painting"],
    imageSet: servicePhotos["exterior-painting"],
    description:
      "Arizona sun is brutal on paint. We repaint stucco, block, siding, fascia, and trim with elastomeric and premium acrylic coatings rated for desert heat and UV — so your home holds its color for years instead of chalking out in two summers. Every exterior job starts with a full pressure wash and crack repair, because paint over dirt is money thrown away.",
    features: [
      "Stucco, block, wood siding & fascia",
      "Elastomeric and UV-resistant desert-grade coatings",
      "Stucco crack and hairline repair before painting",
      "Full pressure wash included with every exterior job",
      "Garage doors, shutters, gates & security doors",
      "HOA color approval assistance",
    ],
    process: [
      { step: "Inspection", desc: "We check stucco, caulking, and fascia for damage that needs repair first." },
      { step: "Wash & Repair", desc: "Surfaces are pressure washed, cracks filled, and loose paint scraped." },
      { step: "Prime & Coat", desc: "Bare spots are primed, then two coats of desert-rated exterior paint go on." },
      { step: "Clean & Review", desc: "Site cleaned, overspray checked, and we walk the property with you." },
    ],
  },
  {
    slug: "power-washing",
    title: "Power Washing",
    subtitle: "Driveways, patios, walls & walkways",
    price: "Free Estimate",
    // Stock photo (Pexels, commercial-use license) — the client photo set has no
    // power-washing shots, and an unrelated driveway photo would misrepresent it.
    image: stockOgPhotos["power-washing"],
    imageSet: stockPhotos["power-washing"],
    description:
      "Years of Arizona dust, hard-water staining, and oil spots come off in a single visit. We pressure wash driveways, walkways, patios, pool decks, block walls, stucco, and commercial storefronts using the right pressure and detergent for each surface — enough to strip the grime, never enough to damage the finish underneath.",
    features: [
      "Driveways, sidewalks & walkways",
      "Patios, pool decks & block walls",
      "House stucco and siding wash-downs",
      "Oil, rust & hard-water stain treatment",
      "Commercial storefronts and parking areas",
      "Surface-safe pressure settings on every job",
    ],
    process: [
      { step: "Surface Check", desc: "We identify each surface type and set the pressure that's safe for it." },
      { step: "Pre-Treat", desc: "Oil, rust, and organic stains get a targeted detergent soak first." },
      { step: "Wash", desc: "Even, overlapping passes so there are no stripes or missed sections." },
      { step: "Rinse & Inspect", desc: "Everything is rinsed clean and reviewed with you before we pack up." },
    ],
  },
  {
    slug: "sun-screens",
    title: "Sun Screens",
    subtitle: "Custom-fit shade screens that cut heat",
    price: "Free Estimate",
    // Client photo: home with solar screens fitted across every window.
    image: serviceOgPhotos["sun-screens"],
    imageSet: servicePhotos["sun-screens"],
    description:
      "Solar sun screens block up to 90% of the heat before it ever reaches your glass — which means cooler rooms, lower APS and SRP bills, and furniture that doesn't fade. We measure each window individually, build the frames to fit, and install them so they look like they came with the house.",
    features: [
      "Custom-measured and built per window",
      "Blocks up to 90% of solar heat gain",
      "Lower summer cooling bills",
      "Protects flooring and furniture from UV fading",
      "Added daytime privacy without blocking your view",
      "Choice of frame and mesh colors",
    ],
    process: [
      { step: "Measure", desc: "Every window is measured individually — no two openings are assumed equal." },
      { step: "Choose", desc: "You pick the mesh density and frame color to match your exterior." },
      { step: "Build", desc: "Frames are cut and assembled to your exact measurements." },
      { step: "Install", desc: "Screens are mounted, squared, and checked for a clean, tight fit." },
    ],
  },
  {
    slug: "rain-gutter-installation",
    title: "Rain Gutter Installation",
    subtitle: "Seamless gutters & downspouts",
    price: "Free Estimate",
    // Stock photo (Pexels, commercial-use license) — no gutter work appears in the
    // client photo set, so a generic house shot would not represent this service.
    image: stockOgPhotos["rain-gutter-installation"],
    imageSet: stockPhotos["rain-gutter-installation"],
    description:
      "Monsoon season moves a lot of water fast. Seamless rain gutters route it away from your stucco, foundation, and landscaping instead of letting it sheet down your walls. We size the gutter and downspout runs to your roof, install them level and secure, and match the color to your fascia or trim.",
    features: [
      "Seamless gutters formed to your roofline",
      "Downspouts routed away from the foundation",
      "Monsoon-season capacity sizing",
      "Color-matched to fascia and trim",
      "Gutter repair, resealing & replacement",
      "Fascia repair and painting available together",
    ],
    process: [
      { step: "Roof Measure", desc: "We map the roofline and calculate the run and downspout placement." },
      { step: "Color Match", desc: "Gutter color is matched to your existing fascia, trim, or paint scheme." },
      { step: "Install", desc: "Seamless sections are hung level, sealed, and fastened to the fascia." },
      { step: "Water Test", desc: "We run water through the system to confirm flow and drainage." },
    ],
  },
  {
    slug: "pergolas",
    title: "Pergolas & Patio Covers",
    subtitle: "Custom-built outdoor shade structures",
    price: "Free Estimate",
    // Client photo: finished patio cover / shade structure on a repainted home.
    image: serviceOgPhotos["pergolas"],
    imageSet: servicePhotos["pergolas"],
    description:
      "Turn an unusable summer patio into the best room in the house. We design and build custom pergolas, patio covers, and shade structures sized to your yard, then finish them with the same paint and stain quality we bring to every other job. Built to handle Arizona heat, wind, and monsoon rain.",
    features: [
      "Custom pergolas sized to your patio",
      "Attached patio covers & freestanding structures",
      "Wood and metal framing options",
      "Sealed, stained, or painted to match your home",
      "Built for desert heat, wind & monsoon loads",
      "Design help from first sketch to final coat",
    ],
    process: [
      { step: "Design", desc: "We measure the space and sketch a structure that fits your yard and budget." },
      { step: "Materials", desc: "You choose framing, finish, and coverage before anything is ordered." },
      { step: "Build", desc: "Our crew frames and assembles the structure on site, level and square." },
      { step: "Finish", desc: "Everything is sanded, sealed, and painted or stained to match your home." },
    ],
  },
];
